import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { 
  ArrowLeft, HelpCircle, ChevronDown, Shield, Brain, 
  CreditCard, Lock, RefreshCw, Users, Pill, AlertTriangle
} from 'lucide-react';

const faqs = [
  {
    category: 'General',
    icon: HelpCircle,
    questions: [
      {
        q: 'Is Vitala free to use?',
        a: 'Yes! Vitala is completely free. You can take our health assessment, receive personalized supplement recommendations, track your symptoms, and use all journaling features at no cost.'
      },
      {
        q: 'Who is Vitala for?',
        a: 'Vitala is designed for adults (18+) who want personalized guidance on supplements. Whether you\'re new to supplements or looking to optimize your current routine, our AI can help you make informed decisions. However, always consult a healthcare provider before starting any new supplement regimen.'
      },
      {
        q: 'How long does the assessment take?',
        a: 'The health assessment takes approximately 5-10 minutes to complete. It includes questions about your health goals, current medications, lifestyle habits, and any symptoms you\'re experiencing.'
      }
    ]
  },
  {
    category: 'AI & Accuracy',
    icon: Brain,
    questions: [
      {
        q: 'How accurate are the AI recommendations?',
        a: 'Our AI (GPT-5.2) is trained on extensive health and nutrition data, but it\'s important to understand its limitations. The recommendations are educational suggestions based on your input—not medical diagnoses. AI can occasionally produce inaccuracies, which is why we always recommend consulting a healthcare professional before starting any supplements.'
      },
      {
        q: 'Does Vitala replace my doctor?',
        a: 'Absolutely not. Vitala is an informational tool designed to help you have better conversations with your healthcare provider. We strongly encourage you to share your results with your doctor or pharmacist before starting any supplement regimen, especially if you have pre-existing conditions or take medications.'
      },
      {
        q: 'How does Vitala check for medication interactions?',
        a: 'When you enter your current medications, our AI analyzes potential interactions with recommended supplements. We flag known interactions and provide warnings. However, this is not exhaustive—your pharmacist or doctor should review your complete medication list for the most accurate interaction check.'
      }
    ]
  },
  {
    category: 'Privacy & Security',
    icon: Lock,
    questions: [
      {
        q: 'Is my health data safe?',
        a: 'Yes. We take your privacy seriously. Your data is stored securely on encrypted databases. We use a "stateless" AI model, meaning your health information is only sent to the AI at the moment of analysis and is not used to train AI models. We never sell your data to third parties.'
      },
      {
        q: 'Can I delete my data?',
        a: 'Yes. You have full control over your data. You can export all your information in JSON format from your Profile page, and you can permanently delete your account and all associated data at any time.'
      },
      {
        q: 'Who can see my results?',
        a: 'Only you can see your results by default. You can choose to share them with your healthcare provider via email or a shareable link. Shared links can be set to expire, and you control who receives them.'
      }
    ]
  },
  {
    category: 'Supplements',
    icon: Pill,
    questions: [
      {
        q: 'Why did Vitala recommend this supplement?',
        a: 'Each recommendation is based on your assessment responses. The AI considers your health goals, current symptoms, lifestyle factors (sleep, stress, diet), age, and any deficiency indicators. You can see the reasoning behind each suggestion in your results.'
      },
      {
        q: 'How often should I retake the assessment?',
        a: 'We recommend retaking the assessment every 3 months, or whenever you experience significant changes in your health, medications, or lifestyle. This helps ensure your supplement protocol stays aligned with your current needs.'
      },
      {
        q: 'Can I take all the recommended supplements together?',
        a: 'Not always. Some supplements are better absorbed when taken separately or at specific times of day. Your results include timing recommendations (morning, afternoon, evening, bedtime) to optimize absorption and prevent interactions. Always follow the timing guidelines provided.'
      }
    ]
  },
  {
    category: 'Safety',
    icon: AlertTriangle,
    questions: [
      {
        q: 'Are supplements safe?',
        a: 'Supplements can be safe when used appropriately, but they\'re not risk-free. They can interact with medications, may not be suitable for certain health conditions, and quality varies by manufacturer. This is why we emphasize consulting healthcare professionals and checking for interactions.'
      },
      {
        q: 'What if I experience side effects?',
        a: 'If you experience any adverse effects, stop taking the supplement immediately and consult a healthcare provider. You should also report the issue to your doctor and consider updating your Vitala assessment to reflect the experience.'
      },
      {
        q: 'Is Vitala FDA approved?',
        a: 'Vitala is an informational platform, not a medical device, so FDA approval doesn\'t apply to us directly. However, it\'s important to note that dietary supplements themselves are not FDA-approved for efficacy—they\'re regulated differently than medications. We recommend choosing supplements from reputable manufacturers that follow Good Manufacturing Practices (GMP).'
      }
    ]
  }
];

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-16 sm:w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6">
            <HelpCircle className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Find answers to common questions about Vitala, our AI recommendations, and supplement safety.
          </p>
        </motion.div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqs.map((category, categoryIndex) => (
            <motion.section
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
              className="bg-white rounded-2xl border border-border overflow-hidden"
            >
              {/* Category Header */}
              <div className="bg-gray-50 px-4 sm:px-6 py-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold text-[#1A1A1A]">{category.category}</h2>
                </div>
              </div>

              {/* Questions */}
              <div className="divide-y divide-border">
                {category.questions.map((item, questionIndex) => {
                  const isOpen = openItems[`${categoryIndex}-${questionIndex}`];
                  return (
                    <div key={questionIndex}>
                      <button
                        onClick={() => toggleItem(categoryIndex, questionIndex)}
                        className="w-full px-4 sm:px-6 py-4 text-left flex items-start justify-between gap-4 hover:bg-gray-50 transition-colors"
                        data-testid={`faq-${categoryIndex}-${questionIndex}`}
                      >
                        <span className="font-medium text-[#1A1A1A] text-sm sm:text-base">{item.q}</span>
                        <ChevronDown 
                          className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`} 
                        />
                      </button>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="px-4 sm:px-6 pb-4"
                        >
                          <p className="text-sm text-muted-foreground leading-relaxed pl-0 sm:pl-4 border-l-2 border-primary/20 sm:ml-2">
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.section>
          ))}
        </div>

        {/* Still Have Questions */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-10 bg-primary/5 border border-primary/20 rounded-2xl p-6 sm:p-8 text-center"
        >
          <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">Still have questions?</h3>
          <p className="text-muted-foreground mb-4">
            Can't find what you're looking for? We're here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <Users className="w-4 h-4" />
              Contact Support
            </Link>
            <Link 
              to="/about" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-border text-[#1A1A1A] rounded-full hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Learn About Vitala
            </Link>
          </div>
        </motion.section>
      </main>
    </div>
  );
}
